// client/src/components/pages/WeighbridgeCompliance/MaintenanceSchedule.tsx
// (converted from .js in the WBE batch, ST-00223)
//
// WBE-13(a): nothing ever stores 'Overdue', so the Overdue tile stayed 0 and a
// past-due job still showed "Scheduled", while the e-mail check and the trends
// treated it as overdue. The badges, the filter and the tiles now use the server's
// effective_status ('Overdue' once a Scheduled job's date has passed, SAST) and its
// tenant-wide `summary` (the tiles used to count only the 20 rows on screen). The
// type filter went out as `maintenanceType`, which the server now honours.
import ThemedSelect from '../../common/ThemedSelect';
import { useState, useEffect, useCallback, type ComponentType } from 'react';
import { Wrench, Plus, Calendar, AlertTriangle, CheckCircle, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { parseISO } from 'date-fns';
import type { AxiosResponse } from 'axios';
import { formatAppDate } from '../../../utils/dateUtils';
import api from '../../../services/weighbridgeComplianceApi';
import { MaintenanceModal as MaintenanceModalJs } from './MaintenanceModal';
import type {
  ComplianceAssetRow,
  MaintenanceListBody,
  MaintenanceRow,
  MaintenanceSummary,
  PaginatedBody,
} from '../../../types/clientPages/weighbridgeCompliance';

// MaintenanceModal is still JS: a typed view of its props.
const MaintenanceModal = MaintenanceModalJs as unknown as ComponentType<{
  record: MaintenanceRow | null;
  assets: ComplianceAssetRow[];
  onClose: () => void;
  onSave: () => void;
}>;

const MAINTENANCE_TYPES = [
  'Daily Check',
  'Weekly Check',
  'Monthly Inspection',
  'Annual Service',
  'Repair',
  'Ad-hoc',
];
const STATUS_OPTIONS = ['Scheduled', 'Completed', 'Overdue', 'Cancelled'];

/** The status to show and count: the server's effective status, else the stored one. */
export const effectiveStatus = (r: MaintenanceRow): string => r.effective_status || r.status;

/** The tiles when the server sent no summary: counted from the rows on screen. */
export function summaryFromRows(rows: MaintenanceRow[], now = new Date()): MaintenanceSummary {
  return {
    scheduled: rows.filter((r) => effectiveStatus(r) === 'Scheduled').length,
    overdue: rows.filter((r) => effectiveStatus(r) === 'Overdue').length,
    completed_this_month: rows.filter((r) => {
      if (r.status !== 'Completed' || !r.completed_date) return false;
      const d = parseISO(r.completed_date);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length,
  };
}

const SkeletonRow = () => (
  <tr>
    {Array.from({ length: 10 }).map((_, i) => (
      <td key={i} className="px-5 py-3.5">
        <div
          className="h-4 bg-slate-200 rounded animate-pulse"
          style={{ width: `${50 + Math.random() * 50}%` }}
        />
      </td>
    ))}
  </tr>
);

export const MaintenanceSchedule = () => {
  const [records, setRecords] = useState<MaintenanceRow[]>([]);
  const [summary, setSummary] = useState<MaintenanceSummary | null>(null);
  const [assets, setAssets] = useState<ComplianceAssetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [assetFilter, setAssetFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MaintenanceRow | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (assetFilter) params.assetId = assetFilter;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.maintenanceType = typeFilter;
      const response = (await api.maintenance.getAll(params)) as AxiosResponse<
        MaintenanceListBody | MaintenanceRow[]
      >;
      const d = response.data;
      setRecords(Array.isArray(d) ? d : d?.data || []);
      setSummary(Array.isArray(d) ? null : d?.summary || null);
    } catch (error) {
      // One message per failing endpoint, however often the filters reload it (ST-00259).
      toast.error('Failed to load maintenance records', { toastId: 'compliance-maintenance-load' });
      console.error('Error fetching maintenance:', error);
    } finally {
      setLoading(false);
    }
  }, [assetFilter, statusFilter, typeFilter]);

  const fetchAssets = useCallback(async () => {
    try {
      const response = (await api.assets.getAll({ limit: 100 })) as AxiosResponse<
        PaginatedBody<ComplianceAssetRow> | ComplianceAssetRow[]
      >;
      const d = response.data;
      setAssets(Array.isArray(d) ? d : d?.data || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleDelete = async (id: number) => {
    try {
      await api.maintenance.delete(id);
      toast.success('Maintenance record deleted');
      setDeleteConfirm(null);
      fetchRecords();
    } catch {
      toast.error('Failed to delete maintenance record');
    }
  };

  const handleEdit = (record: MaintenanceRow) => {
    setEditingRecord(record);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditingRecord(null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingRecord(null);
  };

  const handleModalSave = () => {
    handleModalClose();
    fetchRecords();
  };

  const tiles = summary || summaryFromRows(records);
  const scheduledCount = tiles.scheduled;
  const overdueCount = tiles.overdue;
  const completedThisMonth = tiles.completed_this_month;

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      Scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
      Completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      Overdue: 'bg-red-50 text-red-700 border-red-200',
      Cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
    };
    const dots: Record<string, string> = {
      Scheduled: 'bg-blue-500',
      Completed: 'bg-emerald-500',
      Overdue: 'bg-red-500',
      Cancelled: 'bg-slate-400',
    };
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${styles[status] || styles.Scheduled}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${dots[status] || dots.Scheduled}`} />
        {status}
      </span>
    );
  };

  const getPriorityBadge = (priority: string | null | undefined) => {
    const styles: Record<string, string> = {
      Low: 'bg-slate-100 text-slate-500 border-slate-200',
      Medium: 'bg-blue-50 text-blue-700 border-blue-200',
      High: 'bg-amber-50 text-amber-700 border-amber-200',
      Critical: 'bg-red-50 text-red-700 border-red-200',
    };
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border ${(priority && styles[priority]) || styles.Medium}`}
      >
        {priority}
      </span>
    );
  };

  const getTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      'Daily Check': 'bg-slate-100 text-slate-600',
      'Weekly Check': 'bg-blue-50 text-blue-600',
      'Monthly Inspection': 'bg-indigo-50 text-indigo-600',
      'Annual Service': 'bg-purple-50 text-purple-600',
      Repair: 'bg-amber-50 text-amber-600',
      'Ad-hoc': 'bg-slate-100 text-slate-600',
    };
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${colors[type] || colors['Ad-hoc']}`}
      >
        {type}
      </span>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className="text-lg font-bold text-slate-800"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Maintenance Schedule
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Track and manage weighbridge maintenance activities
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-xs font-semibold flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> Schedule Maintenance
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-surface rounded-xl border border-slate-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-800">{scheduledCount}</p>
            <p className="text-xs text-slate-500">Scheduled</p>
          </div>
        </div>
        <div
          className={`bg-surface rounded-xl border ${overdueCount > 0 ? 'border-red-200 bg-red-50/30' : 'border-slate-200'} p-4 flex items-center gap-3`}
        >
          <div
            className={`w-10 h-10 rounded-lg ${overdueCount > 0 ? 'bg-red-50' : 'bg-slate-50'} flex items-center justify-center`}
          >
            <AlertTriangle
              className={`w-5 h-5 ${overdueCount > 0 ? 'text-red-500' : 'text-slate-400'}`}
            />
          </div>
          <div>
            <p
              className={`text-2xl font-bold ${overdueCount > 0 ? 'text-red-600' : 'text-slate-800'}`}
            >
              {overdueCount}
            </p>
            <p className="text-xs text-slate-500">Overdue</p>
          </div>
        </div>
        <div className="bg-surface rounded-xl border border-slate-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-emerald-500" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-800">{completedThisMonth}</p>
            <p className="text-xs text-slate-500">Completed This Month</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <ThemedSelect
            value={assetFilter}
            onChange={(e) => setAssetFilter(String(e.target.value))}
            className="pl-3 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none bg-surface"
          >
            <option value="">All Assets</option>
            {assets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div className="relative">
          <ThemedSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(String(e.target.value))}
            className="pl-3 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none bg-surface"
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div className="relative">
          <ThemedSelect
            value={typeFilter}
            onChange={(e) => setTypeFilter(String(e.target.value))}
            className="pl-3 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none bg-surface"
          >
            <option value="">All Types</option>
            {MAINTENANCE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </ThemedSelect>
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-100">
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Asset
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Type
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Scheduled Date
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Completed Date
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Performed By
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Status
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Priority
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Findings
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Re-verify
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={10}>
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                        <Wrench className="w-8 h-8 text-slate-300" />
                      </div>
                      <h3 className="text-base font-semibold text-slate-700 mb-1">
                        No maintenance records found
                      </h3>
                      <p className="text-sm text-slate-400 max-w-sm">
                        {assetFilter || statusFilter || typeFilter
                          ? 'Try adjusting your filter criteria'
                          : 'Schedule your first maintenance activity to get started'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                records.map((rec) => (
                  <tr
                    key={rec.id}
                    className={`hover:bg-primary-50/30 cursor-pointer transition-colors ${effectiveStatus(rec) === 'Overdue' ? 'bg-red-50/40' : ''}`}
                    onClick={() => handleEdit(rec)}
                  >
                    <td className="px-5 py-3.5 text-sm text-slate-700 font-medium">
                      {rec.asset_name || '-'}
                    </td>
                    <td className="px-5 py-3.5">{getTypeBadge(rec.maintenance_type)}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-700">
                      {rec.scheduled_date ? formatAppDate(rec.scheduled_date) : '-'}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-700">
                      {rec.completed_date ? formatAppDate(rec.completed_date) : '-'}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-700">
                      {rec.performed_by || '-'}
                    </td>
                    <td className="px-5 py-3.5">{getStatusBadge(effectiveStatus(rec))}</td>
                    <td className="px-5 py-3.5">{getPriorityBadge(rec.priority)}</td>
                    <td
                      className="px-5 py-3.5 text-sm text-slate-700 max-w-[200px] truncate"
                      title={rec.findings || ''}
                    >
                      {rec.findings
                        ? rec.findings.length > 50
                          ? rec.findings.substring(0, 50) + '...'
                          : rec.findings
                        : '-'}
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      {rec.re_verification_required ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
                          <AlertTriangle className="w-3.5 h-3.5" /> Yes
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">No</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(rec);
                          }}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                          title="Edit record"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirm(rec.id);
                          }}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                          title="Delete record"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800">Delete Record</h3>
                <p className="text-sm text-slate-500">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm text-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <MaintenanceModal
          record={editingRecord}
          assets={assets}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};
